import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, ArrowUpRight, ArrowDownLeft, ExternalLink, Search, Filter } from 'lucide-react';
import Navigation from '@/components/Navigation';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CryptoHistoryPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, sent, received

  useEffect(() => {
    loadTransactions();
  }, []);

  useEffect(() => {
    filterTransactions();
  }, [searchTerm, filterType, transactions]);

  const loadTransactions = async () => {
    try {
      const token = localStorage.getItem('routeflow_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/crypto-tx/history/${user.id}?limit=100`, config);
      setTransactions(res.data);
    } catch (error) {
      console.error('Error loading transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const filterTransactions = () => {
    let filtered = transactions;

    // Filter by type
    if (filterType === 'sent') {
      filtered = filtered.filter(tx => tx.from_user_id === user.id);
    } else if (filterType === 'received') {
      filtered = filtered.filter(tx => tx.to_user_id === user.id);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(tx =>
        tx.to_identifier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tx.tx_hash?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tx.currency?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredTransactions(filtered);
  };

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <Navigation user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <Button
          variant="ghost"
          onClick={() => navigate('/crypto/wallet')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Wallet
        </Button>

        <Card className="mb-8">
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <CardTitle className="text-2xl">Transaction History</CardTitle>
                <CardDescription>View all your crypto transfers</CardDescription>
              </div>
              <Button
                onClick={loadTransactions}
                variant="outline"
              >
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by recipient, hash, or currency..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant={filterType === 'all' ? 'default' : 'outline'}
                  onClick={() => setFilterType('all')}
                  size="sm"
                >
                  All
                </Button>
                <Button
                  variant={filterType === 'sent' ? 'default' : 'outline'}
                  onClick={() => setFilterType('sent')}
                  size="sm"
                >
                  Sent
                </Button>
                <Button
                  variant={filterType === 'received' ? 'default' : 'outline'}
                  onClick={() => setFilterType('received')}
                  size="sm"
                >
                  Received
                </Button>
              </div>
            </div>

            {/* Transactions List */}
            {filteredTransactions.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No transactions found</p>
                <Button
                  onClick={() => navigate('/crypto/send')}
                  className="mt-4 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                >
                  Send Your First Transfer
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTransactions.map((tx, index) => {
                  const isSent = tx.from_user_id === user.id;
                  
                  return (
                    <div
                      key={index}
                      className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4 flex-1">
                          {/* Icon */}
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isSent ? 'bg-red-100' : 'bg-green-100'
                          }`}>
                            {isSent ? (
                              <ArrowUpRight className="w-6 h-6 text-red-600" />
                            ) : (
                              <ArrowDownLeft className="w-6 h-6 text-green-600" />
                            )}
                          </div>

                          {/* Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <p className="font-semibold text-gray-900">
                                {isSent ? 'Sent' : 'Received'}
                              </p>
                              <Badge className={getStatusColor(tx.status)}>
                                {tx.status}
                              </Badge>
                            </div>
                            
                            <p className="text-sm text-gray-600 mb-1">
                              {isSent ? `To: ${tx.to_identifier}` : `From: ${formatAddress(tx.to_address)}`}
                            </p>
                            
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              <span>{formatDate(tx.created_at)}</span>
                              <span>•</span>
                              <Badge variant="outline" className="text-xs capitalize">
                                {tx.network}
                              </Badge>
                            </div>

                            {/* Transaction Hash */}
                            {tx.tx_hash && (
                              <div className="mt-2">
                                <a
                                  href={tx.explorer_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center space-x-1"
                                >
                                  <span>View on Explorer</span>
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Amount */}
                        <div className="text-right ml-4">
                          <p className={`text-lg font-bold ${
                            isSent ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {isSent ? '-' : '+'}{tx.amount} {tx.currency}
                          </p>
                          {tx.gas_fee > 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                              Fee: ${tx.gas_fee.toFixed(4)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Summary */}
            {filteredTransactions.length > 0 && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  Showing {filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CryptoHistoryPage;
